import { AvailabilityRequestType, availabilityTypes } from "@app/availability";
import { bookingTypes } from "@app/booking";
import { BookableType, calendarUtils, commonTypes, utilsSearchQuery } from "@app/common";
import { propertyTypes } from "@app/property";
import { propertyGroupTypes } from "@app/propertyGroup/";
import { UtilsDom, UtilsIdentifier } from "@hotelchamp/common";
import { addMonths, differenceInDays, format } from "date-fns";

import { shadowRoot } from "../../bootstrapper";
import { config as appConfig } from "../config";
import { getInitialFormState } from "../context/BookingEngineStateProvider";
import { propertyDetectionService } from "../services";
import { IBookingEngineState, IIbeConfig } from "../types";
import { resolveValidLanguage } from "./languageDetector";

// const encoded = {
//     d: "2023-09-19,2023-09-21", // dates fromDate,toDate
//     br: "2435816bas,2435816br541,2433216b65tr,243581338uts,1,1:0,2:1|243581214esw", // id,packageId,roomId,rateId,adultsCount,childCount,infantCount|extra_1
// };

const datesRegex = /\d{4}-\d{2}-\d{2},\d{4}-\d{2}-\d{2}/;
const bookingRegex =
    /^((?:([a-zA-Z0-9_]{10}|_),([a-zA-Z0-9]{12}|_),([a-zA-Z0-9]{12}|_),([a-zA-Z0-9]{12}|_),\d+(?:,(?:\d+:\d+)(?:,\d+:\d+)*)?(?:\|[a-zA-Z0-9]{12}(?:,[a-zA-Z0-9]{12})*)?)(?:;|$))+$/;

export interface IResolvedConfiguration {
    propertyId: string | undefined;
    propertyGroupId: string | undefined;
}

/**
 * resolvePropertyId
 * Simple method to resolve property id from global scope
 * @returns string | undefined
 */
export const resolvePropertyId = () => ((window as any)[appConfig.globalVendorNamespace] as any)?.ibe?.propertyId;

/**
 * resolvePropertyGroupId
 * Simple method to resolve property group id from global scope
 * @returns string | undefined
 */
export const resolvePropertyGroupId = () => ((window as any)[appConfig.globalVendorNamespace] as any)?.ibe?.propertyGroupId;

export const detectProperty = (propertyGroupToParse: propertyGroupTypes.IPropertyGroup) => {
    const currentUrl = window.location.href;

    propertyDetectionService.setPropertyGroup(propertyGroupToParse);

    return propertyDetectionService.resolveProperty(currentUrl);
};

/**
 * resolveIbeConfiguration
 * Simple method to resolve the configuration
 * @see - resolvePropertyId
 * @see - resolvePropertyGroupId
 * Will throw an Error once property could not be resolved
 * @returns
 */
export const resolveIbeConfiguration = (): IResolvedConfiguration => {
    const propertyId = resolvePropertyId();
    const propertyGroupId = resolvePropertyGroupId();

    if (!propertyId && !propertyGroupId && !window.Cypress) {
        throw new Error("Could not resolve propertyId OR propertyGroupId");
    }

    return {
        propertyId,
        propertyGroupId,
    };
};

export interface IStateToUrlParamsOptions {
    whitelist?: string[];
}

export const stateToUrlParams = (state: IBookingEngineState, { whitelist }: IStateToUrlParamsOptions = {}): string => {
    const bookingState = state.bookingState || {};
    const params = new URLSearchParams();

    if (!Object.keys(bookingState).length) {
        return params.toString();
    }

    if (state.language) {
        params.set("lg", state.language);
    }

    if (state.currency) {
        params.set("cu", state.currency);
    }

    // Promocodes are removed from url params and stored in global state
    params.delete("pc");

    if (state.corpCode) {
        params.set("cc", state.corpCode);
    } else {
        params.delete("cc");
    }

    if (state.bookingId) {
        params.set("id", state.bookingId);
    } else {
        params.delete("id");
    }

    if (bookingState.dates?.fromDate && bookingState.dates?.toDate) {
        params.set("d", `${bookingState.dates.fromDate},${bookingState.dates.toDate}`);
    }

    // Collect all booking parameters into an array
    const brParams = (bookingState.bookings || []).map((b) => {
        const packageId = getShortId(b.package?.id || "") || "_";
        const roomId = getShortId(b.room?.id || "") || "_";
        const rateId = getShortId(b.rate?.id || "") || "_";

        const childCountStr = Object.entries(b.childCount)
            .map(([categoryId, count]) => `${categoryId}:${count}`)
            .join(",");

        let br = `${b.id},${packageId},${roomId},${rateId},${b.adultsCount}${!!childCountStr.length ? `,${childCountStr}` : ""}`;

        if (b.extras.length) {
            br = `${br}|${b.extras.map((e) => getShortId(e.id)).join(",")}`;
        }

        return br;
    });

    // Join all bookings with semicolons and set a single 'br' parameter
    if (brParams.length > 0) {
        params.set("br", brParams.join(";"));
    }

    if (whitelist) {
        const entries = Array.from(params.entries());

        entries.forEach(([key, value]) => {
            if (!whitelist.includes(key)) {
                params.delete(key);
            }
        });
    }

    setFilterUrlParams(params, state.bookableItemsFilter);

    return params.toString();
};

/**
 * generateUniqueBookingSelectionHash
 * Helper to generate a unique hash for the booking selection based on the state.
 * This is useful to compare 2 hashes and determine wheter the booking selection has changed
 * and some follow up actions should be taken, e.g. when the hash stored in the checkout session
 * state doen't match the current hash then we should clear create a new checkout session
 *
 * @param state
 * @returns
 */
export const generateUniqueBookingSelectionHash = (state: IBookingEngineState): string =>
    stateToUrlParams(state, { whitelist: ["pc", "d", "br"] });

export const getAvailabilitySearch = (search: URLSearchParams, requestType?: AvailabilityRequestType): URLSearchParams => {
    const params = new URLSearchParams();

    params.set("rt", String(requestType || AvailabilityRequestType.Get));

    const [ci, co] = (search.get("d") || "").split(",");

    params.set("ci", ci);
    params.set("co", co);

    const pc = search.get("pc");
    const cc = search.get("cc");

    if (pc) {
        params.set("pc", pc);
    }
    if (cc) {
        params.set("cc", cc);
    }

    // Get booking rooms from the new semicolon-separated format
    let bookingRooms: string[] = [];
    // Check for the new br parameter format first
    const brValue = search.get("br");
    if (brValue) {
        bookingRooms = brValue.split(";");
    }

    if (!bookingRooms.length) {
        bookingRooms = ["2435816bas,_,_,_,2,1:0,2:0"];
    }

    bookingRooms.forEach((br: string, i: number) => {
        const [bookings] = br.split("|");
        const props = (bookings || "").split(",");

        params.set(`rs[${i}][id]`, props[0]);
        params.set(`rs[${i}][ac]`, props[4]);

        // Parse child categories
        const childCounts = props.slice(5);

        childCounts.forEach((countStr) => {
            const [categoryId, count] = countStr.split(":");
            params.set(`rs[${i}][cc][${categoryId}]`, count);
        });
    });

    return params;
};

export const stateToAvailabilitySearchParams = (state: IBookingEngineState, requestType?: AvailabilityRequestType): URLSearchParams => {
    const bookingState = state.bookingState;
    const params = new URLSearchParams();

    params.set("rt", String(requestType || AvailabilityRequestType.Get));

    if (bookingState?.dates?.fromDate && bookingState?.dates?.toDate) {
        params.set("ci", bookingState.dates.fromDate);
        params.set("co", bookingState.dates.toDate);
    }

    if (state.promoCode) {
        params.set("pc", state.promoCode);
    }

    if (state.corpCode) {
        params.set("cc", state.corpCode);
    }

    if (requestType && requestType > 0) {
        params.set("lg", state.language);
        params.set("cu", state.currency);
    }

    (bookingState?.bookings || []).forEach((b, i) => {
        params.set(`rs[${i}][id]`, b.id);
        params.set(`rs[${i}][ac]`, b.adultsCount.toString());

        Object.entries(b.childCount).forEach(([categoryId, count]) => {
            params.set(`rs[${i}][cc][${categoryId}]`, count.toString());
        });

        if (requestType && requestType > 0 && b.rate?.id && b.room?.id) {
            params.set(`rs[${i}][ra]`, b.rate.id.toString());
            params.set(`rs[${i}][ro]`, b.room.id.toString());

            b.extras.forEach((extra, j) => {
                params.set(`rs[${i}][ex][${j}]`, extra.id);
            });
        }
    });

    return params;
};

/**
 * Helper to validate availability search params.
 * Doesn't match server side validation 1 on 1 but sufficient for client side
 * mainly to determine whether state is valid to go into checkout flow
 * @param searchParams
 * @returns
 */
export const isAvailabilitySearchParamsValidForRequestType = (searchParams: URLSearchParams): boolean => {
    const requestType = searchParams.get("rt") ? (Number(searchParams.get("rt")) as AvailabilityRequestType) : undefined;

    const hasValue = (value: any) => value !== undefined && value !== "undefined";

    let isValid = true;

    // has valid request type
    isValid = isValid && Object.values(AvailabilityRequestType).includes(requestType || "");

    // has valid from and to date
    isValid = isValid && hasValue(searchParams.get("ci")) && hasValue(searchParams.get("co"));

    // promo code (pc) is optional

    // corp code (cc) is optional

    if (requestType && [AvailabilityRequestType.Check, AvailabilityRequestType.Get].includes(requestType)) {
        const searchParamsObject = utilsSearchQuery.constructObjectBySearchParams(searchParams);

        // should have language (lg) and currency (cu)
        isValid = isValid && hasValue(searchParams.get("lg")) && hasValue(searchParams.get("cu"));

        const bookings = searchParamsObject?.rs || [];

        // should have at least one booking
        isValid = isValid && bookings.length > 0;

        if (bookings.length) {
            // should have at least one booking with room and rate
            isValid =
                isValid &&
                !bookings.some((booking: any) => {
                    // return true if found incomplete booking

                    // booking should have adults count
                    const hasAdultsCount = hasValue(booking?.ac) && hasValue(booking?.ro);

                    // a booking should have rate id and room id
                    const hasRoomAndRate = hasValue(booking?.ra) && hasValue(booking?.ro);

                    return !hasAdultsCount || !hasRoomAndRate;
                });
        }
    }

    return isValid;
};

export const urlParamsToState = (
    params: string,
    property: propertyTypes.IProperty | undefined,
    availability: availabilityTypes.IAvailability[],
    extras: commonTypes.IExtra[][],
    calendarAvailability: availabilityTypes.ICalendarAvailability
): IBookingEngineState | null => {
    const q = new URLSearchParams(params);

    if (!property) {
        return null;
    }

    if (!availability.length && !params.includes("id=")) {
        return null;
    }

    const initialState = getInitialFormState(property);

    if (!validUrlParams(q)) {
        return initialState;
    }

    const [fromDate, toDate] = (q.get("d") || "").split(",");

    const availableDates = calendarAvailability ? calendarUtils.availableDateRange(calendarAvailability, fromDate, toDate) : true;
    const language = resolveValidLanguage(q.get("lg") || "", property?.languages, initialState.language).resolvedLanguage;

    const state: IBookingEngineState = {
        ...initialState,
        bookableItemsFilter: urlParamsToFilterState(q, property),
        language,
        currency: q.get("cu") || initialState.currency,
        promoCode: q.get("pc") || initialState.promoCode,
        corpCode: q.get("cc") || initialState.corpCode,
        bookingState: {
            bookings: initialState.bookingState?.bookings || [],
            dates: { fromDate, toDate },
        },
        bookingId: q.get("id") || undefined,
        activeStep: q.get("c") || initialState.activeStep,
        activeWarningKey: !availableDates ? "date" : null,
    };

    // Process the new single 'br' parameter with semicolon-separated bookings
    const brValue = q.get("br");

    if (brValue) {
        const bookingsData = brValue.split(";");
        bookingsData.forEach((br, brIndex) => {
            const [bookings, extraIds] = br.split("|");
            const props = (bookings || "").split(",");
            const type = props[1] === "_" ? "room" : "package";
            const foundPackage = (availability[brIndex]?.packages || []).find((p) => matchShortId(p.id, props[1])) || null;
            const foundRoom = (availability[brIndex]?.rooms || []).find((r) => matchShortId(props[2], r.id)) || null;
            const foundRate = getRateByShortId(type === "room" ? foundRoom : foundPackage, props[3]) || null;
            const selectedExtras = getSelectedExtras(extras[brIndex], (extraIds || "").split(","));

            const childCount: { [categoryId: string]: number } = {};

            const childCounts = props.slice(5);
            childCounts.forEach((countStr) => {
                const [categoryId, count] = countStr.split(":");
                childCount[categoryId] = Number(count);
            });

            state.bookingState.bookings[brIndex] = {
                id: props[0] === "_" ? UtilsIdentifier.uniqueString() : props[0],
                type,
                package: foundPackage,
                room: foundRoom,
                rate: foundRate,
                adultsCount: Number(props[4]),
                childCount,
                isConfirmed: Number(props[7]) === 1 ? true : false,
                extras: selectedExtras,
                price: undefined,
                discountedPrice: undefined,
            };

            const roomWarning = props[2] !== "_" && !foundRoom;
            const rateWarning = props[3] !== "_" && !foundRate;

            state.activeWarningKey = roomWarning ? "room" : rateWarning ? "rate" : state.activeWarningKey;
        });
    }

    // q.getAll("br").forEach((br, i) => {
    //     const [bookings, extraIds] = br.split("|");
    //     const props = (bookings || "").split(",");
    //     const type = props[1] === "_" ? "room" : "package";
    //     const foundPackage = (availability[i]?.packages || []).find((p) => matchShortId(p.id, props[1])) || null;
    //     const foundRoom = (availability[i]?.rooms || []).find((r) => matchShortId(props[2], r.id)) || null;
    //     const foundRate = getRateByShortId(type === "room" ? foundRoom : foundPackage, props[3]) || null;
    //     const selectedExtras = getSelectedExtras(extras[i], (extraIds || "").split(","));

    //     const childCount: { [categoryId: string]: number } = {};

    //     const childCounts = props.slice(5);
    //     childCounts.forEach((countStr) => {
    //         const [categoryId, count] = countStr.split(":");
    //         childCount[categoryId] = Number(count);
    //     });

    //     state.bookingState.bookings[i] = {
    //         id: props[0] === "_" ? UtilsIdentifier.uniqueString() : props[0],
    //         type,
    //         package: foundPackage,
    //         room: foundRoom,
    //         rate: foundRate,
    //         adultsCount: Number(props[4]),
    //         childCount,
    //         isConfirmed: Number(props[7]) === 1 ? true : false,
    //         extras: selectedExtras,
    //         price: undefined,
    //         discountedPrice: undefined,
    //     };

    //     const roomWarning = props[2] !== "_" && !foundRoom;
    //     const rateWarning = props[3] !== "_" && !foundRate;

    //     state.activeWarningKey = roomWarning ? "room" : rateWarning ? "rate" : state.activeWarningKey;
    // });

    return state;
};

export const getBookingRoomRate = (params: string, availability: availabilityTypes.IAvailability[]) => {
    if (!params || !availability.length) {
        return [];
    }

    const q = new URLSearchParams(params);
    const rateRoomIds: string[][] = [];
    // Process the new semicolon-separated br parameter
    const brValue = q.get("br");
    if (brValue) {
        const bookingsData = brValue.split(";");
        bookingsData.forEach((value, brIndex) => {
            const [bookings] = value.split("|");
            const props = (bookings || "").split(",");
            const type = props[1] === "_" ? "room" : "package";
            const foundPackage = (availability[brIndex]?.packages || []).find((p) => matchShortId(p.id, props[1])) || null;
            const foundRoom = (availability[brIndex]?.rooms || []).find((r) => matchShortId(props[2], r.id)) || null;
            const foundRate = getRateByShortId(type === "room" ? foundRoom : foundPackage, props[3]) || null;

            rateRoomIds.push(foundRoom && foundRate ? [foundRoom.id, foundRate.id] : []);
        });
    }

    return rateRoomIds;
};

export const getRateByShortId = (
    item: availabilityTypes.IRoomAvailability | availabilityTypes.IPackageAvailability | null,
    shortId: string
): availabilityTypes.IRateAvailability | undefined =>
    item?.bookable_type === BookableType.Room
        ? (item?.rates || []).find((r) => matchShortId(shortId, r.id))
        : (item?.rooms[0]?.rates || []).find((r) => matchShortId(shortId, r.id));

export const getSelectedExtras = (extras: commonTypes.IExtra[], extraShortIds: string[]) =>
    (extras || []).filter((e) => extraShortIds.includes(getShortId(e.id) || ""));

export const bookingHasExtras = (params: string): boolean => {
    const q = new URLSearchParams(params);

    // Check the new semicolon-separated br parameter format
    const brValue = q.get("br");
    if (brValue && brValue.includes("|")) {
        return true;
    }

    return false;
};

export const urlParamsToFilterState = (
    search: URLSearchParams,
    property: propertyTypes.IProperty | undefined
): commonTypes.IBookableItemsFilter => {
    if (!property) {
        return { type: undefined, filters: [] };
    }
    const queryFilterType = search.get("f[type]");
    const type =
        queryFilterType === "_" || !queryFilterType
            ? undefined
            : queryFilterType === "rp"
            ? "rp"
            : queryFilterType === "r"
            ? BookableType.Room
            : BookableType.Package;

    const filters = !!search.get("f[filters]")
        ? (search.get("f[filters]") || "").split(",").map((id) => getFullId(id, "filter", property) || id)
        : [];

    return {
        type,
        filters,
    };
};

export const setFilterUrlParams = (params: URLSearchParams, filter: commonTypes.IBookableItemsFilter) => {
    if (filter) {
        const type = filter.type === undefined ? null : filter.type === "rp" ? "rp" : filter.type === BookableType.Room ? "r" : "p";

        if (type) {
            params.set("f[type]", type);
        } else {
            params.delete("f[type]");
        }

        if (filter.filters.length) {
            params.set("f[filters]", filter.filters.map((id) => getShortId(id)).join(","));
        } else {
            params.delete("f[filters]");
        }
    }
};

export const matchShortId = (shortId: string, uuid: string): boolean => uuid.split("-")[4] === shortId;

export const getShortId = (id: string | null): string | null => {
    if (!id) {
        return null;
    }

    if (id.length < 8) {
        throw new Error("getShortId is used with valid UUIDs only");
    }

    return id.split("-")[4];
};

export const getFullId = (
    shortId: string,
    type: "room" | "package" | "rate" | "extra" | "filter",
    property: propertyTypes.IProperty
): string | null => {
    const key = `${type}s`;

    if (!shortId) {
        return null;
    }

    if (type === "filter") {
        return property.filter_groups.flatMap((fg) => fg.filters).find((f) => matchShortId(shortId, f.id))?.id || null;
    }

    return property[key].map((r: any) => r.id).find((id: string) => matchShortId(shortId, id)) || null;
};

export const validUrlParams = (q: URLSearchParams): boolean => {
    const regexMap: { [key: string]: RegExp } = {
        d: datesRegex,
        br: bookingRegex,
    };

    // To be enforced with additional checks, works for now
    if (!q.has("p") || !q.has("c")) {
        return false;
    }

    for (const key of q.keys()) {
        if (key in regexMap) {
            for (const value of q.getAll(key)) {
                if (!regexMap[key].test(value)) {
                    return false;
                }
            }
        }
    }

    return true;
};

export const calendarRange = {
    fromDate: format(new Date(), "yyyy-MM-dd"),
    toDate: format(addMonths(new Date(), 6), "yyyy-MM-dd"),
};

interface IPreloadPropertyImagesOptions {
    primaryOnly?: boolean;
    mode?: "all" | "package" | "room";
}

export const preloadPropertyImages = (
    property: propertyTypes.IProperty,
    { primaryOnly = true, mode = "room" }: IPreloadPropertyImagesOptions = {}
) => {
    const collectedPrimaryImages: commonTypes.IImage[] = [];
    const preloadContainerElement = shadowRoot;

    const collectImages = (items: propertyTypes.IProperty["rooms"] | propertyTypes.IProperty["packages"]) => {
        items?.forEach((item) => {
            if (item?.images?.length) {
                if (primaryOnly) {
                    collectedPrimaryImages.push(item.images[0]);
                } else {
                    item?.images.forEach((image) => collectedPrimaryImages.push(image));
                }
            }
        });
    };

    if (["all", "room"].includes(mode)) {
        collectImages(property?.rooms || []);
    }

    if (["all", "package"].includes(mode)) {
        collectImages(property?.packages || []);
    }

    const existingPreloadedImageFilenames = Array.from(document.querySelectorAll<HTMLLinkElement>("hc-ibe-preload-img")).map(
        (link) => link.href
    );

    collectedPrimaryImages.forEach((image) => {
        if (!existingPreloadedImageFilenames.includes(image.path)) {
            UtilsDom.createElement(preloadContainerElement as any, "link", {
                rel: "preload",
                href: image.path,
                as: "image",
                id: `hc-ibe-preload-img-${image.id}`,
                class: "hc-ibe-preload-img",
            });
        }
    });
};

export const reservedBookingToCart = (reservedBooking: bookingTypes.IReservedBooking): bookingTypes.ICart => ({
    total: String(reservedBooking.total),
    discounted_total: String(reservedBooking.discounted_total),
    fees: reservedBooking.cart_items.filter((item) => item.key.includes("res-fee")),
    taxes: reservedBooking.cart_items.filter((item) => item.key.includes("tax")),
    rooms: reservedBooking.cart_items.filter((item) => item.key.includes("rate")),
    discounts: reservedBooking.cart_items.filter((item) => item.key.includes("promocode")),
    extras: reservedBooking.cart_items.filter((item) => item.key.includes("supplement")),
    refundable: reservedBooking.refundable,
    refund_amount: reservedBooking.refund_amount,
    cancellation_within_window: reservedBooking.cancellation_within_window,
});

export const ibeConfigToUrlParams = (config: IIbeConfig): URLSearchParams => {
    const params = new URLSearchParams();

    params.set("p", "base");

    const { checkin, checkout, adultCount, infantCount, childCount, roomCount, promoCode, languageCode, propertyId } = config;

    if (checkin && checkout) {
        params.set("d", `${format(checkin, "yyyy-MM-dd")},${format(checkout, "yyyy-MM-dd")}`);
    }

    // Generate booking rooms based on roomCount
    const brParams = [];
    for (let i = 0; i < (roomCount || 1); i++) {
        brParams.push(`${UtilsIdentifier.uniqueString()},_,_,_,${adultCount || 2},1:${infantCount || 0},2:${childCount || 0}`);
    }
    // Set a single 'br' parameter with all room bookings joined by semicolons
    if (brParams.length > 0) {
        params.set("br", brParams.join(";"));
    }

    if (promoCode) {
        params.set("pc", promoCode);
    }

    if (languageCode) {
        params.set("lg", languageCode);
    }

    if (checkin && checkout && adultCount) {
        params.set("c", "availability");
    } else {
        params.set("c", "search");
    }

    return params;
};

export const ibeStateToTrackingSystemBooking = (
    state: IBookingEngineState,
    availability: availabilityTypes.IAvailability[],
    gd: bookingTypes.IGuestDetails | null,
    cart: bookingTypes.ICart | null
) => {
    // Ensure the ibe state is valid
    if (!state.bookingState?.bookings?.length || !state.bookingState?.dates || !availability.length) {
        return null;
    }

    const arrivalDate = new Date(state.bookingState.dates.fromDate);
    const departureDate = new Date(state.bookingState.dates.toDate);
    // const babyCount = (state.bookingState?.bookings || []).reduce((sum, b) => sum + b.infantCount, 0);
    const minRoomRate = availability[0].rooms.reduce((min, el) => (el.min_rate <= min ? el.min_rate : min), Infinity);
    const maxRoomRate = availability[0].rooms.reduce((max, el) => (el.max_rate >= max ? el.max_rate : max), 0);
    const avgRoomRate = (minRoomRate + maxRoomRate) / 2;

    const [totalAdultCountForAllRooms, totalChildCountForAllRooms, totalBabyCountForAllRooms] = (state.bookingState.bookings || []).reduce(
        ([currentTotalAdultCount, currentTotalChildCount, currentTotalBabyCount], booking) => {
            const bookingTotalChildCount = Object.values(booking.childCount).reduce((sum, count) => sum + count, 0);

            return [
                currentTotalAdultCount + booking.adultsCount,
                currentTotalChildCount + bookingTotalChildCount, // Sum of all child categories
                currentTotalBabyCount + (booking.childCount["1"] || 0), // Assuming category 1 is for infants/babies
            ];
        },
        [0, 0, 0]
    );

    return {
        arrivalDate,
        departureDate,
        adultCount: totalAdultCountForAllRooms,
        childCount: totalChildCountForAllRooms,
        babyCount: totalBabyCountForAllRooms,
        stayNights: differenceInDays(new Date(departureDate), new Date(arrivalDate)),
        currencyCode: state.currency,
        roomCount: state.bookingState.bookings.length,
        rateValues: {
            min: minRoomRate,
            avg: avgRoomRate,
            max: maxRoomRate,
        },
        roomsRates: availability[0].rooms.map((room) => ({
            title: room.name,
            rates: room.rates.map((r) => ({ title: r.name, rate: r.rate })),
        })),
        bookingPrice: Number(cart?.total) || null,
        promoCode: state.promoCode,
        email: gd?.email || null,
        languageCode: state.language,
        roomType: state.bookingState.bookings[0].room?.name || null,
        roomRateType: state.bookingState.bookings[0].rate?.name || null,
        upsellProducts: state.bookingState.bookings[0].extras.map((ex) => ex.name),
        isRoomAvailability: true,
    };
};

export const isIbeParam = (key: string) =>
    ["d", "lg", "cu", "pc", "cc", "br[", "p", "c", "id", "pi", "f[type]", "f[filters]", "fo"].includes(key);

export const mergeSearchParams = (oldParams: URLSearchParams, newParams: URLSearchParams): URLSearchParams => {
    const mergedParams = new URLSearchParams();

    // Add all params from params1 to the mergedParams
    oldParams.forEach((value, key) => {
        // If params2 doesn't have this key and this key is not an ibe param key, retain it from params1
        // If the param is an ibe param and was removed, we don't want to add it again.
        if (!newParams.has(key) && !isIbeParam(key)) {
            mergedParams.append(key, value);
        }
    });

    // Add all params from params2 to the mergedParams, replacing any existing keys
    newParams.forEach((value, key) => {
        // For all ibe params, we want to replace the existing value except for the br param which can have multiple values
        const method = key === "br" ? "append" : "set";

        mergedParams[method](key, value);
    });

    return mergedParams;
};

export const getRouteChildValue = () => new URLSearchParams(window.location.search).get("c");

/**
 * Helper function to determine whether a route page (child) is currently active
 * @param page
 * @returns
 */
export const isRoutePageActive = (page: string) => getRouteChildValue() === page;

export const isOneOfRoutePagesActive = (pages: string[]) => pages.some((page) => isRoutePageActive(page));

export const getIsRoutePageActiveMap = (routeChildren: string[]) => {
    const activeChild = getRouteChildValue();

    return routeChildren.reduce(
        (accumulator, child) => ({
            ...accumulator,
            [child]: activeChild === child,
        }),
        {}
    );
};
